import React, { forwardRef, useState } from "react";
import { Plus, Minus } from "lucide-react";
import clsx from "clsx";

export interface NumberInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  label?: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "filled" | "outlined";
  showButtons?: boolean;
  className?: string;
}

/**
 * TracQ Number Input Component
 * Modern number input with optional +/- buttons
 * - TracQ Brand Colors & Typography
 * - Customizable step, min, max values
 * - Optional increment/decrement buttons
 * - Dark Mode optimized
 */
export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      value,
      onChange,
      min = 0,
      max = 999999,
      step = 1,
      placeholder,
      label,
      helperText,
      error = false,
      disabled = false,
      size = "md",
      variant = "default",
      showButtons = true,
      className,
      ...props
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState(value.toString());

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);

      const numValue = parseFloat(newValue);
      if (!isNaN(numValue) && numValue >= min && numValue <= max) {
        onChange(numValue);
      }
    };

    const handleIncrement = () => {
      const newValue = Math.min(value + step, max);
      onChange(newValue);
      setInputValue(newValue.toString());
    };

    const handleDecrement = () => {
      const newValue = Math.max(value - step, min);
      onChange(newValue);
      setInputValue(newValue.toString());
    };

    const handleBlur = () => {
      // Ensure value is within bounds on blur
      const numValue = parseFloat(inputValue);
      if (isNaN(numValue) || numValue < min) {
        onChange(min);
        setInputValue(min.toString());
      } else if (numValue > max) {
        onChange(max);
        setInputValue(max.toString());
      } else {
        onChange(numValue);
        setInputValue(numValue.toString());
      }
    };

    const variantStyles = {
      default:
        "bg-slate-900/50 border-slate-700/50 focus:border-tracq-bright-blue focus:ring-1 focus:ring-tracq-bright-blue/30",
      filled:
        "bg-slate-800/80 border-transparent focus:border-tracq-bright-blue focus:ring-1 focus:ring-tracq-bright-blue/30",
      outlined:
        "bg-transparent border border-slate-600 focus:border-tracq-bright-blue focus:ring-1 focus:ring-tracq-bright-blue/30",
    };

    const sizeStyles = {
      sm: "px-3 py-2 text-sm h-8",
      md: "px-3 py-2.5 text-sm h-10",
      lg: "px-4 py-3 text-base h-12",
    };

    const buttonSizeStyles = {
      sm: "w-6 h-6",
      md: "w-7 h-7",
      lg: "w-8 h-8",
    };

    const errorStyles = error ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : "";

    return (
      <div className={clsx("w-full", className)}>
        {label && <label className="block text-sm font-medium text-slate-300 mb-2">{label}</label>}

        <div className="relative flex items-center">
          {showButtons && (
            <button
              type="button"
              onClick={handleDecrement}
              disabled={disabled || value <= min}
              className={clsx(
                "absolute left-1 z-10 rounded-sm",
                "bg-slate-800/80 border border-slate-600/50",
                "hover:bg-slate-700/80 hover:border-slate-500",
                "focus:outline-none focus:ring-1 focus:ring-tracq-bright-blue/50",
                "disabled:opacity-40 disabled:cursor-not-allowed",
                "transition-all duration-150 ease-out",
                buttonSizeStyles[size],
                "flex items-center justify-center p-0",
              )}
            >
              <Minus className="w-3 h-3 text-slate-300" />
            </button>
          )}

          <input
            ref={ref}
            type="number"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            min={min}
            max={max}
            step={step}
            className={clsx(
              // Modern Input Base Styles
              "w-full rounded-md font-poppins text-center",
              "text-white placeholder-slate-500",
              "border transition-all duration-150 ease-out",
              "focus:outline-none focus:ring-offset-0",
              "disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-slate-800/30",
              // Hide browser number input arrows
              "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",

              // Variant & Size Styles
              variantStyles[variant],
              sizeStyles[size],

              // Error State
              errorStyles,

              // Button spacing
              showButtons ? "pl-9 pr-9" : "px-3",
            )}
            {...props}
          />

          {showButtons && (
            <button
              type="button"
              onClick={handleIncrement}
              disabled={disabled || value >= max}
              className={clsx(
                "absolute right-1 z-10 rounded-sm",
                "bg-slate-800/80 border border-slate-600/50",
                "hover:bg-slate-700/80 hover:border-slate-500",
                "focus:outline-none focus:ring-1 focus:ring-tracq-bright-blue/50",
                "disabled:opacity-40 disabled:cursor-not-allowed",
                "transition-all duration-150 ease-out",
                buttonSizeStyles[size],
                "flex items-center justify-center p-0",
              )}
            >
              <Plus className="w-3 h-3 text-slate-300" />
            </button>
          )}
        </div>

        {helperText && (
          <p
            className={clsx(
              "mt-1.5 text-sm font-poppins",
              error ? "text-red-400" : "text-slate-400",
            )}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

NumberInput.displayName = "NumberInput";
